// Authentication Context
// UPDATED: Google sign-in now uses GIS SDK (signInWithIdToken) — no redirect needed
// FIXED: signOut now clears all user data and session storage
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import {
  getSession,
  onAuthStateChange,
  sendOTP as sendOTPService,
  verifyOTP as verifyOTPService,
  signOut as signOutService,
} from '../services/authService';
import { getMyPlans } from '../services/planService';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loadSavedPlan, setLoadSavedPlan] = useState(null); // Callback to load plan
  // FIX: Track if we're in a pending save flow
  const [pendingSaveContext, setPendingSaveContext] = useState(false);
  // FIX: Callback to reset UserDataContext
  const [resetUserDataCallback, setResetUserDataCallback] = useState(null);
  // Track previous user ID to detect account switches
  const prevUserIdRef = useRef(null);

  // Helper: clear all finncal localStorage keys (prevents data leaking between accounts)
  const clearAllFinncalStorage = () => {
    sessionStorage.clear();
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (
        key &&
        (key.startsWith('finncal_') ||
          key === 'currentPlan' ||
          key === 'savedPlanId' ||
          key === 'premium_unlocked' ||
          key === 'premium_email')
      ) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  };

  // Initialize auth state
  useEffect(() => {
    const initAuth = async () => {
      try {
        const session = await getSession();
        if (session?.user) {
          prevUserIdRef.current = session.user.id;
          setUser(session.user);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Subscribe to auth changes
    const subscription = onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // FIX: Detect user identity changes and reset stale data
  useEffect(() => {
    const currentId = user?.id || null;
    const prevId = prevUserIdRef.current;

    // User changed to a DIFFERENT user (account switch)
    if (currentId && prevId && currentId !== prevId) {
      console.log('User identity changed, resetting all data for new user');
      clearAllFinncalStorage();
      if (resetUserDataCallback) {
        resetUserDataCallback();
      }
    }

    // User signed out
    if (!currentId && prevId) {
      if (resetUserDataCallback) {
        resetUserDataCallback();
      }
    }

    prevUserIdRef.current = currentId;
  }, [user, resetUserDataCallback]);

  // Auto-load saved plan when user signs in
  useEffect(() => {
    const autoLoadPlan = async () => {
      if (user && loadSavedPlan) {
        try {
          // Always reset context before loading new user's data
          if (resetUserDataCallback) {
            resetUserDataCallback();
          }

          const result = await getMyPlans();
          if (result.success && result.data && result.data.length > 0) {
            const latestPlan = result.data[0]; // Most recent plan
            loadSavedPlan(latestPlan); // Call the callback to load into context
          }
        } catch (error) {
          console.error('Error auto-loading plan:', error);
        }
      }
    };

    autoLoadPlan();
  }, [user, loadSavedPlan, resetUserDataCallback]);

  /**
   * Send OTP to email
   */
  const sendOTP = async (email) => {
    const result = await sendOTPService(email);
    return result;
  };

  /**
   * Verify OTP
   */
  const verifyOTP = async (email, token) => {
    const result = await verifyOTPService(email, token);
    if (result.success) {
      setUser(result.user);
      setShowAuthModal(false);
    }
    return result;
  };

  /**
   * Sign in with Google
   * With GIS SDK, GoogleAuthButton handles auth directly via signInWithIdToken.
   * This method now just opens the auth modal — the button inside does the rest.
   */
  const signInWithGoogle = async () => {
    setShowAuthModal(true);
    return { success: true };
  };

  /**
   * Sign out
   * FIXED: Now clears all user data, session storage, and resets contexts
   */
  const signOut = async () => {
    // NOTE: Do NOT delete planning_suite_data on logout — user data should persist across sessions

    // Tell Google GIS to not auto-select the account next time
    try {
      window.google?.accounts?.id?.disableAutoSelect();
    } catch (e) {
      /* GIS not loaded, ignore */
    }

    const result = await signOutService();
    if (result.success) {
      // Clear user state
      setUser(null);

      // Clear ALL finncal-related storage (comprehensive cleanup)
      clearAllFinncalStorage();

      // Reset UserDataContext if callback is set
      if (resetUserDataCallback) {
        resetUserDataCallback();
      }

      // Force page reload to clear all state
      window.location.href = '/';
    }
    return result;
  };

  /**
   * Require authentication - show modal if not authenticated
   */
  const requireAuth = (callback) => {
    if (user) {
      callback();
    } else {
      setShowAuthModal(true);
    }
  };

  /**
   * FIX: Show auth modal with pending save context
   * Call this when user tries to save but isn't authenticated
   */
  const showAuthForSave = () => {
    setPendingSaveContext(true);
    setShowAuthModal(true);
  };

  /**
   * FIX: Clear pending save context (call after modal closes)
   */
  const clearPendingSaveContext = () => {
    setPendingSaveContext(false);
  };

  const value = {
    user,
    loading,
    showAuthModal,
    setShowAuthModal,
    sendOTP,
    verifyOTP,
    signInWithGoogle,
    signOut,
    requireAuth,
    isAuthenticated: !!user,
    setLoadSavedPlan,
    // FIX: New methods for pending save flow
    pendingSaveContext,
    showAuthForSave,
    clearPendingSaveContext,
    // FIX: Allow UserDataContext to register its reset function
    setResetUserDataCallback,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
